import { useSelector } from 'react-redux';

import Button, { IconButton } from '@components/button/Button';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import CustomTable from '@components/custom-table/CustomTable';
import { Icon, Input, Tooltip } from '@components/index';
import {
  GROUP_MEMBER_MAX_AMOUNT,
  GROUP_MEMBER_MIN_AMOUNT
} from '@constants/generic';
import {
  getCustomTermMemberTableColumn,
  getCustomTermTableColumn
} from '@features/group-management/constants';
import { getFeeCategoryDataFromConfig } from '@features/group-management/helper';
import {
  isDateBefore,
  onAddTerm,
  onTermDelete
} from '@features/group-management/helpers/customTerm';
import {
  CustomTermTableProps,
  FeeConfigurationType,
  MemberDiscountLine,
  TermsList
} from '@types';
import { roundTo2 } from '@utils/generic';

import MemberCustomTermDiscountTooltipContent from './MemberCustomTermDiscountTooltipContent';
import TermDueAmountField from './TermDueAmountField';
import TermDueDateSelection from './TermDueDateSelection';

import theme from '../../../../../../themes/colors';

const discountTotalFromLines = (lines: MemberDiscountLine[]) =>
  lines.reduce((sum, line) => sum + Number(line.amount ?? 0), 0);

const CustomTermTable = ({
  termsList,
  setTermsList,
  isFeeCategoryEnabled,
  feeCategories,
  onAmountChange,
  isMember = false,
  isEditGroup = false,
  error,
  setError,
  memberFlow,
  groupEndDateValidationMessage,
  splitHeaderAmountToTerms = false,
  onEndSplitHeaderAmountMode,
  onStartSplitHeaderAmountMode,
  defaultTermLabel,
  className,
  memberDiscounts
}: CustomTermTableProps) => {
  const disabledRowIds: string[] = [];
  const { orgConfig } = useSelector((state: any) => state.user);

  const groupMemberMaxAmount =
    orgConfig?.group_member_amount_max || GROUP_MEMBER_MAX_AMOUNT;
  const groupMemberMinAmount =
    orgConfig?.group_member_amount_min || GROUP_MEMBER_MIN_AMOUNT;

  const handleAddTerm = () => {
    const { term } = onAddTerm(termsList, feeCategories, defaultTermLabel);
    const shouldRedistribute =
      !isMember && !isEditGroup && splitHeaderAmountToTerms;
    setTermsList(term, shouldRedistribute);
  };

  const handleResetAmount = () => {
    const resetList = termsList.map((term) => ({
      ...term,
      dueAmount: '0'
    }));
    setTermsList(resetList);
    onAmountChange('');
    onStartSplitHeaderAmountMode?.();
  };

  const handleTermDelete = (termId: string) => {
    const { termsList: updatedTermsList, totalAmount } = onTermDelete(
      termId,
      termsList
    );

    const shouldRedistribute =
      !isMember && !isEditGroup && splitHeaderAmountToTerms;

    setTermsList(updatedTermsList, shouldRedistribute);

    if (!shouldRedistribute) {
      onAmountChange(String(totalAmount));
    }
  };

  const handleTermFieldEdit = (
    termId: string,
    field: string,
    value: any,
    userInitiated?: boolean
  ) => {
    if (field === 'dueAmount' && userInitiated) {
      onEndSplitHeaderAmountMode?.();
    }

    const updatedTermsList = termsList.map((termDetails) =>
      termDetails.id === termId
        ? { ...termDetails, [field]: value }
        : termDetails
    );
    setTermsList(updatedTermsList);

    if (field === 'dueAmount') {
      setError?.({
        ...(error ?? {}),
        dueDate: '',
        dueDateRowId: '',
        termDueAmount: '',
        termDueAmountRowId: ''
      });
      let totalAmount = 0;
      updatedTermsList.forEach((termDetails) => {
        if (termDetails.id === termId) {
          totalAmount += Number(value);
        } else totalAmount += Number(termDetails.dueAmount);
      });
      totalAmount = roundTo2(totalAmount);

      const skipHeaderSync =
        splitHeaderAmountToTerms && userInitiated === false;

      if (!skipHeaderSync) {
        onAmountChange(String(totalAmount));
      }
    }
  };

  const onFeeCategoryAmountSave = (
    termFeeConfig: FeeConfigurationType[],
    termIndex: number
  ) => {
    onEndSplitHeaderAmountMode?.();
    const term = termsList[termIndex];
    const termFeeCategoryData = getFeeCategoryDataFromConfig(termFeeConfig);
    const newTotalAmount = roundTo2(
      termFeeCategoryData.reduce(
        (sum, category) => sum + (Number(category.value) || 0),
        0
      )
    );

    const updatedTermsList = termsList.map((termDetails) =>
      termDetails.id === term.id
        ? {
            ...termDetails,
            feeCategoryData: termFeeConfig,
            dueAmount: String(newTotalAmount)
          }
        : termDetails
    );

    const totalAmount = roundTo2(
      updatedTermsList.reduce(
        (sum, termDetails) => sum + Number(termDetails.dueAmount),
        0
      )
    );

    setError?.({
      ...(error ?? {}),
      dueDate: '',
      dueDateRowId: '',
      termDueAmount: '',
      termDueAmountRowId: ''
    });
    onAmountChange(String(totalAmount));

    setTermsList(updatedTermsList);
  };

  const isRowDisabled = (row: TermsList) => {
    if (!isMember && !isEditGroup) return false;
    if (memberFlow === 'edit' || isEditGroup)
      return row.isPaymentLinkSent === true;
    return false;
  };

  const hasPerTermDiscounts = termsList.some(
    (t) => (t.applied_group_member_discount_details?.length ?? 0) > 0
  );
  const showAfterDiscountColumn =
    !!isMember &&
    memberFlow === 'edit' &&
    (hasPerTermDiscounts || (memberDiscounts?.length ?? 0) > 0);

  const discountLinesForRow = (row: TermsList): MemberDiscountLine[] => {
    if (hasPerTermDiscounts) {
      return row.applied_group_member_discount_details ?? [];
    }
    return memberDiscounts ?? [];
  };

  const checkIsDueDateError = (row: TermsList) =>
    !!error?.dueDate && error.dueDateRowId === row.id;

  const modifiedTableData = termsList?.map((row: TermsList, index) => {
    const isDisabled = isRowDisabled(row);

    if (isDisabled) disabledRowIds.push(row.id);

    const isDueDateError = checkIsDueDateError(row);

    const dueAmountNum = Number(row.dueAmount);

    const grossForDiscountRow = Number.isFinite(dueAmountNum)
      ? dueAmountNum
      : Number(row.originalAmountRequestedDetails?.total_amount ?? 0);

    const rowDiscountLines = discountLinesForRow(row);
    const rowDiscountTotal = discountTotalFromLines(rowDiscountLines);
    const afterDiscountAmountValue = grossForDiscountRow - rowDiscountTotal;
    const discountTooltipId = `member-custom-term-disc-${row.id}`;

    const isThisRowAmountError =
      !!error?.termDueAmount &&
      error.termDueAmountRowId === row.id &&
      !!error.termDueAmount.trim();

    const dueDate =
      !isEditGroup && !isMember && isDateBefore(row.dueDate, new Date())
        ? ''
        : row.dueDate;

    return {
      id: row.id,
      term_label: (
        <div
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
        >
          <Input
            value={row.term_label}
            handleInputChange={(value) =>
              handleTermFieldEdit(row.id, 'term_label', value)
            }
            disabled={isDisabled || isMember}
          />
        </div>
      ),
      dueDate: (
        <TermDueDateSelection
          dueDate={dueDate}
          previousTermDueDate={
            index > 0 ? termsList[index - 1]?.dueDate : undefined
          }
          onDueDateChange={(value) => {
            handleTermFieldEdit(row.id, 'dueDate', value);
            setError?.({ ...error, dueDate: '', dueDateRowId: '' });
          }}
          isDueDateDisabled={isDisabled}
          isDueDateError={isDueDateError}
        />
      ),
      amount: (
        <TermDueAmountField
          row={row}
          rowIndex={index}
          isFeeCategoryEnabled={isFeeCategoryEnabled}
          feeCategoryData={row.feeCategoryData || []}
          onFeeCategorySave={(params: FeeConfigurationType[]) =>
            onFeeCategoryAmountSave(params, index)
          }
          onAmountChange={(value, userInitiated) =>
            handleTermFieldEdit(row.id, 'dueAmount', value, userInitiated)
          }
          isDisabled={isDisabled}
          allowDecimals
          isAmountRowError={isThisRowAmountError}
          onTermAmountRowError={(message) => {
            setError?.({
              ...(error ?? {}),
              termDueAmount: message ?? '',
              termDueAmountRowId: message ? row.id : '',
              ...(!message && { dueDate: '', dueDateRowId: '' })
            });
          }}
        />
      ),
      ...(showAfterDiscountColumn && {
        afterDiscountAmount: (
          <div className="inline-flex items-center gap-1.5">
            <CurrencyAmount
              value={afterDiscountAmountValue}
              className="inline-flex items-center"
              symbolClass="font-normal !text-md pr-1"
              valueClass="font-medium !text-md"
            />
            <span
              data-tooltip-id={discountTooltipId}
              className="inline-flex cursor-default"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              role="presentation"
            >
              <Icon name="info-circle" size="12" stroke={theme.cofeeNeutral8} />
            </span>
            <Tooltip
              id={discountTooltipId}
              type="custom"
              variant="dark"
              place="bottom"
              clickable
              className="!z-50 !max-w-[min(92vw,22rem)] !rounded-lg !border-0 !bg-transparent !p-0 !shadow-none"
            >
              <MemberCustomTermDiscountTooltipContent
                originalAmount={grossForDiscountRow}
                discountLines={rowDiscountLines}
                afterDiscountAmount={afterDiscountAmountValue}
              />
            </Tooltip>
          </div>
        )
      }),
      ...(!isMember &&
        !isEditGroup && {
          actions: (() => {
            const isLastDeletable =
              index === termsList.length - 1 && termsList.length > 2;
            return (
              <IconButton
                iconName={isLastDeletable ? 'trash-red-2' : 'trash'}
                variant="transparent"
                size="none"
                className={`m-0 ${
                  !isLastDeletable ? 'cursor-not-allowed opacity-40' : ''
                }`}
                handleClick={() => handleTermDelete(row.id)}
                disabled={!isLastDeletable}
              />
            );
          })()
        }),
      _error_message:
        (isThisRowAmountError ? error.termDueAmount : undefined) ??
        (error?.dueDate && isDueDateError ? error.dueDate : undefined)
    };
  });

  const amountWithIcon = (amount: string, compact = false) => (
    <CurrencyAmount
      value={Number(amount)}
      className={compact ? 'inline-flex items-center' : 'flex items-center'}
      symbolClass="font-normal !text-sm"
      valueClass={
        compact ? 'font-medium !text-sm' : 'pl-1 font-medium !text-sm'
      }
    />
  );

  return (
    <>
      <div
        className={`flex h-fit flex-col justify-start rounded-lg border border-grey30 p-4 ${className}`}
      >
        <CustomTable
          headers={
            isMember || isEditGroup
              ? getCustomTermMemberTableColumn(
                  defaultTermLabel || 'Installment',
                  !!isMember,
                  memberFlow,
                  showAfterDiscountColumn
                )
              : getCustomTermTableColumn(defaultTermLabel || 'Installment')
          }
          data={modifiedTableData}
          disabledRowIds={disabledRowIds}
          className="!pb-0"
          customRowClassName="!min-h-0"
        />
        {!!groupEndDateValidationMessage && (
          <div className="flex items-center gap-2 text-sm text-error">
            <Icon name="alert-circle" size="none" stroke={theme.error} />
            {groupEndDateValidationMessage}
          </div>
        )}
        {!isMember && !isEditGroup && (
          <div className="mt-3 flex items-center justify-between">
            <Button
              variant="secondary"
              label={`Add ${defaultTermLabel || 'Installment'}`}
              handleClick={handleAddTerm}
              leftIconName="plus"
              leftIconStroke={theme.primary}
              className="w-45"
            />
            <Button
              variant="primary"
              label="Reset Amount"
              handleClick={handleResetAmount}
              className="w-40"
            />
          </div>
        )}
      </div>
      <div className="flex flex-col items-start gap-1 pb-3 pt-1 text-base text-grey80">
        <div className="text-base text-grey80">Notes :</div>
        <div className="ml-4 flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-x-2">
            <span>- You can enter any amount between</span>
            {amountWithIcon(groupMemberMinAmount, true)}
            <span>and</span>
            {amountWithIcon(groupMemberMaxAmount, true)}
          </div>
          <div className="flex flex-wrap items-center gap-x-2">
            <span>- If a term doesn&apos;t apply, simply enter</span>
            <span className="inline-flex items-center gap-x-0">
              &quot;{amountWithIcon('0', true)}&quot;
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomTermTable;
